import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, MapPin, Search } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import {
  buildEvents,
  MONTHS_PT,
  formatMonthLabel,
  formatDate,
  formatTime,
} from "@/lib/events";

const EVENTS_DATA = buildEvents();

const CATEGORIES = [
  { value: "all",        label: "Todos",       color: "bg-gray-500" },
  { value: "culto",      label: "Cultos",      color: "bg-[#D4AF37]" },
  { value: "estudo",     label: "Estudos",     color: "bg-blue-500" },
  { value: "jovens",     label: "Jovens",      color: "bg-purple-500" },
  { value: "mulheres",   label: "Mulheres",    color: "bg-pink-500" },
  { value: "infantil",   label: "Infantil",    color: "bg-green-500" },
  { value: "especiais",  label: "Especiais",   color: "bg-red-500" },
  { value: "ministerio", label: "Ministérios", color: "bg-orange-500" },
];

export default function Events() {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const groupedEvents = useMemo(() => {
    const q = searchTerm.toLowerCase();
    const filtered = EVENTS_DATA.filter((ev) => {
      const matchCat = selectedCategory === "all" || ev.category === selectedCategory;
      const matchSearch = !q || ev.title.toLowerCase().includes(q) || ev.description.toLowerCase().includes(q);
      return matchCat && matchSearch;
    });
    return filtered.reduce((acc, ev) => {
      const key = ev.date.slice(0, 7);
      (acc[key] = acc[key] || []).push(ev);
      return acc;
    }, {});
  }, [selectedCategory, searchTerm]);

  const totalCount = Object.values(groupedEvents).flat().length;

  return (
    <main className="bg-[#050505] text-white min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-white mb-6">
              Eventos da <span className="text-[#D4AF37]">PIBG</span>.
            </h1>
            <p className="text-xl text-white/80 leading-relaxed">
              Fique por dentro de todos os acontecimentos da nossa comunidade.
              Cultos, estudos, encontros e eventos especiais.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FILTROS */}
      <section className="py-12 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={20} />
              <input
                type="text"
                placeholder="Buscar eventos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#D4AF37]/50 transition-colors"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    selectedCategory === cat.value
                      ? `${cat.color} text-black`
                      : "bg-[#0A0A0A] text-white/60 hover:text-white border border-white/10"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* LISTA DE EVENTOS */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          {totalCount === 0 ? (
            <div className="text-center py-16">
              <Calendar className="mx-auto text-white/20 mb-4" size={48} />
              <p className="text-white/60 text-lg">Nenhum evento encontrado para os filtros selecionados.</p>
            </div>
          ) : (
            <div className="space-y-12">
              {Object.entries(groupedEvents).map(([monthKey, events]) => (
                <div key={monthKey}>
                  <h2 className="text-xs tracking-widest uppercase text-[#D4AF37] mb-6 pb-3 border-b border-white/5">
                    {formatMonthLabel(monthKey)}
                  </h2>
                  <div className="grid gap-4">
                    {events.map((event, index) => {
                      const [, m, d] = event.date.split("-").map(Number);
                      const timeLabel = formatTime(event.time);

                      return (
                        <motion.article
                          key={event.id}
                          initial={{ opacity: 0, y: 16 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.4) }}
                          className="bg-[#0A0A0A] border border-white/5 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all duration-300"
                        >
                          <div className="flex flex-col sm:flex-row gap-4">
                            {/* bloco de data */}
                            <div className="sm:w-20 flex-shrink-0">
                              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-lg p-3 text-center">
                                <div className="text-2xl font-bold text-[#D4AF37] leading-none">{d}</div>
                                <div className="text-xs text-white/50 mt-1">
                                  {MONTHS_PT[m - 1].slice(0, 3)}
                                </div>
                              </div>
                            </div>

                            {/* conteúdo */}
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                                <h3 className="font-serif-display text-xl text-white">{event.title}</h3>
                                {event.recurring === "weekly" && (
                                  <span className="px-2 py-0.5 bg-white/8 text-white/40 text-[10px] rounded-full border border-white/10">
                                    Semanal
                                  </span>
                                )}
                                {event.recurring === "monthly" && (
                                  <span className="px-2 py-0.5 bg-[#D4AF37]/15 text-[#D4AF37] text-[10px] rounded-full">
                                    Mensal
                                  </span>
                                )}
                              </div>
                              <p className="text-white/55 text-sm mb-3 leading-relaxed">{event.description}</p>
                              <div className="flex flex-wrap gap-4 text-xs text-white/40">
                                {timeLabel && (
                                  <span className="flex items-center gap-1.5">
                                    <Clock size={12} />
                                    {timeLabel}
                                  </span>
                                )}
                                <span className="flex items-center gap-1.5">
                                  <Calendar size={12} />
                                  {formatDate(event.date)}
                                </span>
                                <span className="flex items-center gap-1.5">
                                  <MapPin size={12} />
                                  {event.location}
                                </span>
                              </div>
                            </div>
                          </div>
                        </motion.article>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
